/**
 * The rule the journey is drawing, between the screen and the wire shape.
 *
 * There is deliberately no matching arithmetic in here. An earlier draft of this
 * screen mirrored the ladder locally so a rung could print its cost without a
 * round trip; the engine answers that now — `measure-recent-readings` for what a
 * rung fills over this field's own last readings, and `field-can-match-column`
 * for whether the pairing can ever work — and a second implementation of either
 * would be a number that drifts from the one the documents actually get.
 *
 * What is left is what the screen genuinely owns: the labels and examples on the
 * four spelling switches, and the translation between the five nouns the sentence
 * names and the record the engine stores.
 */

import { lookupSpellingSwitchIds } from '../shared/desktop-api';
import type {
  FieldLookupAssignment,
  LookupReturn,
  LookupRuleDefinition,
  SpellingSwitches,
} from '../shared/desktop-api';
import {
  normalizeValueKind,
  STRICT_SWITCH,
  VALUE_KIND_CODE,
  VALUE_KIND_NAME,
} from './lookup-analysis';

export type JourneyLadder = {
  /** Rung one, always climbed. The switches say what "the same way" means. */
  spelling: SpellingSwitches;
  /** Rung two: close spellings for names, confused glyphs for codes. */
  forgiving: boolean;
  minimumScore: number;
  runnerUpMargin: number;
};

export type JourneyDraft = {
  ruleId: string;
  name: string;
  dataTableId: string;
  /** The field on the document whose reading finds the row. */
  sourceFieldId: string | null;
  findColumn: string;
  returns: LookupReturn[];
  ladder: JourneyLadder;
  onMiss: 'keep' | 'blank' | 'review';
};

/**
 * How many of a field's readings the rungs are counted over.
 *
 * Forty is a number an operator can hold — "fills 31 of 40" is a sentence,
 * "fills 8,412 of 11,003" is arithmetic — and it is the window the specification
 * writes every rung label in.
 */
export const READINGS_COUNTED = 40;

/**
 * What each switch on the first rung is called, and what turning it on makes equal.
 *
 * The ids come from `lookupSpellingSwitchIds` and are never restated here: a
 * second list of them is how a switch an operator turned off comes back on. Only
 * the wording is the screen's, and every switch carries an example because
 * "punctuation is normalised" is a rule and `Ademero, Inc. = Ademero Inc` is the
 * thing the rule does.
 */
const SWITCH_WORDING: Record<string, { label: string; example: string }> = {
  case: { label: 'Upper and lower case', example: 'ademero = ADEMERO' },
  punctuation: { label: 'Full stops and commas', example: 'Ademero, Inc. = Ademero Inc' },
  spaces: { label: 'Extra spaces', example: 'Ademero  Inc = Ademero Inc' },
  'legal-suffix': { label: STRICT_SWITCH[VALUE_KIND_NAME].label, example: 'Ademero Inc = Ademero' },
  separators: { label: 'Dashes, dots and slashes', example: 'VN0300 = VN-0300' },
  'leading-zeros': { label: STRICT_SWITCH[VALUE_KIND_CODE].label, example: 'A01 = A1' },
};

/**
 * The switches the engine lets a rule turn off (DocumentStore refuses the rest: two spellings that
 * differ only in case, punctuation, spaces or separators are one value everywhere in the product).
 * The others are drawn on and fixed, not as ticks that only fail at the trial and at Save.
 */
const SWITCHES_THAT_TURN_OFF: ReadonlySet<string> = new Set(['legal-suffix', 'leading-zeros']);

export const spellingSwitches = (
  kind: string,
): { id: string; label: string; example: string; fixed: boolean }[] =>
  [
    ...(normalizeValueKind(kind) === VALUE_KIND_CODE
      ? lookupSpellingSwitchIds.code
      : lookupSpellingSwitchIds.name),
  ].map((id) => ({
    id,
    ...(SWITCH_WORDING[id] ?? { label: id, example: '' }),
    fixed: !SWITCHES_THAT_TURN_OFF.has(id),
  }));

/** Only the switch ids this kind of value has, out of whatever was stored. */
export const switchesFor = (held: SpellingSwitches, kind: string): SpellingSwitches =>
  Object.fromEntries(spellingSwitches(kind).map((entry) => [entry.id, held[entry.id] !== false]));

/**
 * The held switches with this kind's missing ids added as on, which is what a
 * missing id means. Nothing held is removed or changed: a stored rule names no
 * kind, so the switches of the kind it was saved for must survive the screen
 * guessing another one while the list is still being read.
 */
export const withSwitchesOf = (held: SpellingSwitches, kind: string): SpellingSwitches => {
  const missing = spellingSwitches(kind).filter((entry) => !(entry.id in held));
  return missing.length === 0
    ? held
    : { ...held, ...Object.fromEntries(missing.map((entry) => [entry.id, true])) };
};

/** The rule as the rest of the shell expects it. */
export const toRuleDefinition = (
  draft: JourneyDraft,
  jobId: string,
  kind: string,
): LookupRuleDefinition => ({
  id: draft.ruleId,
  jobId,
  name: draft.name,
  dataTableId: draft.dataTableId,
  findColumn: draft.findColumn,
  returns: draft.returns,
  valueKind: normalizeValueKind(kind),
  matchLadder: {
    exact: true,
    characterConfusion: normalizeValueKind(kind) === VALUE_KIND_CODE && draft.ladder.forgiving,
    fuzzy: normalizeValueKind(kind) !== VALUE_KIND_CODE && draft.ladder.forgiving,
    minimumScore: draft.ladder.minimumScore,
    runnerUpMargin: draft.ladder.runnerUpMargin,
    normalisation: switchesFor(draft.ladder.spelling, kind),
  },
});

/**
 * A stored rule opened back onto the journey.
 *
 * The switches are taken as stored, every id kept. The engine's rule has no kind
 * (the kind is the column's), so filtering them here by a kind guessed before the
 * list is read dropped a code rule's "separators" and "leading-zeros" and brought
 * them back on. `toRuleDefinition` picks the kind's ids when the rule is sent.
 */
export const fromRuleDefinition = (
  rule: LookupRuleDefinition,
): Omit<JourneyDraft, 'sourceFieldId' | 'onMiss'> => ({
  ruleId: rule.id,
  name: rule.name,
  dataTableId: rule.dataTableId,
  findColumn: rule.findColumn,
  // A rule the boundary could not bring forward arrives with none, and is reported
  // as a problem rather than opened as if it filled the column it happens to name.
  returns: (rule.returns ?? []).filter((entry): entry is LookupReturn =>
    Boolean(entry.column && entry.fieldName),
  ),
  ladder: {
    spelling: { ...(rule.matchLadder.normalisation ?? {}) },
    forgiving: rule.matchLadder.characterConfusion || rule.matchLadder.fuzzy,
    minimumScore: rule.matchLadder.minimumScore,
    runnerUpMargin: rule.matchLadder.runnerUpMargin,
  },
});

/**
 * What a saved rule writes onto the fields of the document type it was saved from.
 *
 * Every field the rule fills points at it, is told which reading finds the row, and
 * is given the same behaviour when nothing matches — they came off one row and they
 * answer to one setting. A return that writes the matched column back into the field
 * it was read from is a field held to the list, which the engine vets in place rather
 * than fills: its own reading finds the row, which is no source.
 *
 * Only this rule's assignment is touched. A field can be subject to several rules
 * (the engine runs each), so a field this rule fills keeps its other rules with this
 * one's entry replaced in place, and a field this rule no longer fills — its return
 * removed or pointed at another field — loses this rule's entry and nothing else.
 * Replacing the whole list dropped the other rules; patching only the filled fields
 * left a field the rule stopped filling still assigned to it, blanked on every miss.
 *
 * `switchedFieldId` is the one exception: a field picked under a list's "Use this
 * list for a field" is being moved onto that list, so while this rule fills it, its
 * other lookups go. Keeping them left the old list still filling (or blanking) it.
 */
export const fieldPatchesForSave = (
  fields: readonly { id: string; name: string; lookups?: FieldLookupAssignment[] | null }[],
  draft: Pick<JourneyDraft, 'ruleId' | 'returns' | 'sourceFieldId' | 'findColumn' | 'onMiss'>,
  savedRuleId: string,
  switchedFieldId: string | null = null,
): { fieldId: string; patch: { lookups: FieldLookupAssignment[] } }[] => {
  const fieldOf = (entry: LookupReturn) =>
    (entry.fieldId ? fields.find((field) => field.id === entry.fieldId) : undefined) ??
    fields.find((field) => field.name === entry.fieldName);
  const filled = new Map<string, FieldLookupAssignment>();
  for (const entry of draft.returns) {
    const field = fieldOf(entry);
    if (!field || filled.has(field.id)) continue;
    filled.set(field.id, {
      ruleId: savedRuleId,
      sourceFieldId:
        field.id === draft.sourceFieldId && entry.column === draft.findColumn
          ? null
          : (draft.sourceFieldId ?? null),
      noMatchBehaviour: draft.onMiss,
    });
  }
  const isThisRule = (assignment: FieldLookupAssignment) =>
    assignment.ruleId === savedRuleId || (!!draft.ruleId && assignment.ruleId === draft.ruleId);
  return fields.flatMap((field) => {
    const held = field.lookups ?? [];
    const next = filled.get(field.id);
    if (!next && !held.some(isThisRule)) return [];
    const lookups: FieldLookupAssignment[] = [];
    let placed = false;
    const switched = !!next && field.id === switchedFieldId;
    for (const assignment of held) {
      if (!isThisRule(assignment)) {
        if (!switched) lookups.push(assignment);
      } else if (next && !placed) {
        lookups.push(next);
        placed = true;
      }
    }
    if (next && !placed) lookups.push(next);
    return [{ fieldId: field.id, patch: { lookups } }];
  });
};

/**
 * Whether the rule says enough to be run at all.
 *
 * The boundary refuses an incomplete one by name, which is right for a save and
 * wrong for a keystroke: a journey half-built would otherwise put a refusal in the
 * error bar on every character typed into the trial box. So nothing is asked of
 * the engine until the sentence is complete, and the screen says which noun is
 * missing instead.
 */
export const whatIsMissing = (draft: JourneyDraft): string | null => {
  if (!draft.dataTableId) return 'Choose the list this reading is looked up in.';
  if (!draft.findColumn) return 'Choose the column in the list that is matched against.';
  if (!draft.sourceFieldId) return 'Choose the field on the document whose reading finds the row.';
  if (draft.returns.length === 0) return 'Choose a column to fill a field from.';
  const unnamed = draft.returns.find((entry) => !entry.column || !entry.fieldName);
  if (unnamed) return `Choose the field that ${unnamed.column || 'this column'} fills.`;
  return null;
};
